import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  MessageSquare,
  Search,
  Send,
  Paperclip,
  X,
  ArrowLeft,
  User,
  Building2,
  Briefcase,
  CreditCard,
  Wrench,
  Home,
  ChevronRight,
  Check,
  Clock,
  FileText,
  Download,
  Users,
  Plus
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// User type icons and colors
const userTypeConfig = {
  investor: { icon: Home, color: "text-gold", bg: "bg-gold/20", label: "Investor" },
  realtor: { icon: Building2, color: "text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]", bg: "bg-blue-500/20", label: "Realtor" },
  title: { icon: FileText, color: "text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)]", bg: "bg-purple-500/20", label: "Title Company" },
  lender: { icon: CreditCard, color: "text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]", bg: "bg-emerald-500/20", label: "Lender" },
  contractor: { icon: Wrench, color: "text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]", bg: "bg-amber-500/20", label: "Contractor" }
};

// Avatar component
function UserAvatar({ user, size = "md" }) {
  const config = userTypeConfig[user?.user_type] || userTypeConfig.investor;
  const Icon = config.icon;
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-10 h-10",
    lg: "w-12 h-12"
  };
  
  return (
    <div className={`${sizeClasses[size]} ${config.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
      {user?.picture ? (
        <img src={user.picture} alt="" className="w-full h-full rounded-full object-cover" />
      ) : (
        <Icon className={`w-5 h-5 ${config.color}`} />
      )}
    </div>
  );
}

// Contact search modal
function ContactSearchModal({ isOpen, onClose, onSelect, currentUserId }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [userType, setUserType] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ q: searchTerm });
      if (userType) params.append("user_type", userType);
      const res = await axios.get(`${BACKEND_URL}/api/messaging/contacts/search?${params}`, { withCredentials: true });
      setResults(res.data.filter(r => r.user_id !== currentUserId));
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  const handleBrowseRole = async (role) => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/messaging/contacts/by-role/${role}`, { withCredentials: true });
      setResults(res.data.filter(r => r.user_id !== currentUserId));
      setUserType(role);
    } catch (error) {
      toast.error("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] w-full max-w-md">
        <div className="p-4 border-b border-white/[0.08] flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-white">New Message</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          {/* Search */}
          <div className="flex gap-2 mb-4">
            <Input
              placeholder="Search by name or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              className="bg-black border-white/[0.08] text-white"
            />
            <Button onClick={handleSearch} disabled={loading} className="bg-gold hover:bg-gold/90 text-black">
              <Search className="w-4 h-4" />
            </Button>
          </div>

          {/* Browse by role */}
          <p className="text-white/40 text-xs mb-2">Or browse by role:</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(userTypeConfig).map(([type, cfg]) => {
              const Icon = cfg.icon;
              return (
                <button
                  key={type}
                  onClick={() => handleBrowseRole(type)}
                  className={`px-3 py-1.5 border text-sm flex items-center gap-1 transition-all duration-300 ${
                    userType === type ? `${cfg.bg} border-current ${cfg.color}` : "bg-black border-white/[0.08] text-white/50 hover:border-white/[0.15]"
                  }`}
                >
                  <Icon className="w-3 h-3" /> {cfg.label}s
                </button>
              );
            })}
          </div>

          {/* Results */}
          <div className="max-h-64 overflow-y-auto space-y-2">
            {loading ? (
              <div className="text-center py-4">
                <div className="w-6 h-6 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : results.length > 0 ? (
              results.map((contact) => {
                const config = userTypeConfig[contact.user_type] || userTypeConfig.investor;
                return (
                  <button
                    key={contact.user_id}
                    onClick={() => { onSelect(contact); onClose(); }}
                    className="w-full flex items-center gap-3 p-3 bg-black border border-white/[0.08] hover:border-gold/50 transition-all duration-300 text-left"
                  >
                    <UserAvatar user={contact} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{contact.name || contact.display_name}</p>
                      <p className="text-white/40 text-xs truncate">{contact.email || contact.brokerage || config.label}</p>
                    </div>
                    <span className={`text-xs ${config.color}`}>{config.label}</span>
                  </button>
                );
              })
            ) : searchTerm || userType ? (
              <p className="text-white/40 text-center py-4">No contacts found</p>
            ) : (
              <p className="text-white/40 text-center py-4">Search or browse by role</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Conversation list item
function ConversationItem({ conversation, isActive, onClick }) {
  const other = conversation.other_participant;
  const config = userTypeConfig[other?.user_type] || userTypeConfig.investor;
  
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 p-3 transition-all duration-300 text-left ${
        isActive ? "bg-gold/10 border-l-2 border-gold" : "hover:bg-[#0A0A0A]/80 backdrop-blur-sm"
      }`}
    >
      <UserAvatar user={other} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <p className="text-white font-medium truncate">{other?.name || "Unknown"}</p>
          {conversation.unread_count > 0 && (
            <span className="w-5 h-5 bg-gold text-black text-xs font-bold rounded-full flex items-center justify-center">
              {conversation.unread_count}
            </span>
          )}
        </div>
        <p className="text-white/40 text-sm truncate">
          {conversation.last_message?.content || "No messages yet"}
        </p>
      </div>
    </button>
  );
}

// Message bubble
function MessageBubble({ message, isMine }) {
  return (
    <div className={`flex ${isMine ? "justify-end" : "justify-start"} mb-3`}>
      <div className={`max-w-[70%] ${isMine ? "bg-gold text-black" : "bg-[#0A0A0A]/80 backdrop-blur-sm text-white"} px-4 py-2`}>
        <p className="text-sm">{message.content}</p>
        {message.attachments?.length > 0 && (
          <div className="mt-2 space-y-1">
            {message.attachments.map((att, idx) => (
              <a
                key={idx}
                href={`${BACKEND_URL}/api/messaging/file/${att.file_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 text-xs ${isMine ? "text-white/20" : "text-white/50"} hover:underline`}
              >
                <Paperclip className="w-3 h-3" /> {att.filename}
              </a>
            ))}
          </div>
        )}
        <p className={`text-xs mt-1 ${isMine ? "text-white/30" : "text-white/40"}`}>
          {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
    </div>
  );
}

export default function MessagesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const messagesEndRef = useRef(null);
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Mobile state: show list by default, chat panel when conversation selected
  const [mobileShowChat, setMobileShowChat] = useState(false);

  useEffect(() => {
    fetchData();
    // Poll for new messages
    const interval = setInterval(fetchConversations, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Scroll to bottom on new messages
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    // Check for conversation_id in URL
    const convId = searchParams.get("conversation");
    if (convId && conversations.length > 0) {
      const conv = conversations.find(c => c.conversation_id === convId);
      if (conv) {
        loadConversation(conv);
      }
    }
  }, [searchParams, conversations]);

  const fetchData = async () => {
    try {
      const [userRes, convsRes, unreadRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/messaging/conversations`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/messaging/unread-count`, { withCredentials: true })
      ]);
      setUser(userRes.data);
      setConversations(convsRes.data);
      setUnreadCount(unreadRes.data.unread_count);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchConversations = async () => {
    try {
      const [convsRes, unreadRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/messaging/conversations`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/messaging/unread-count`, { withCredentials: true })
      ]);
      setConversations(convsRes.data);
      setUnreadCount(unreadRes.data.unread_count);
    } catch (error) {
      // Silent fail
    }
  };

  const loadConversation = async (conv) => {
    setActiveConversation(conv);
    setMobileShowChat(true); // Show chat panel on mobile
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/messaging/conversations/${conv.conversation_id}`,
        { withCredentials: true }
      );
      setMessages(res.data.messages);
      // Refresh to update unread counts
      fetchConversations();
    } catch (error) {
      toast.error("Failed to load messages");
    }
  };
  
  // Mobile back button handler
  const handleMobileBack = () => {
    setMobileShowChat(false);
    setActiveConversation(null);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConversation) return;
    
    setSending(true);
    try {
      const other = activeConversation.other_participant;
      await axios.post(`${BACKEND_URL}/api/messaging/send`, {
        recipient_id: other.user_id,
        recipient_type: other.user_type,
        content: newMessage
      }, { withCredentials: true });
      
      setNewMessage("");
      loadConversation(activeConversation);
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleStartConversation = async (contact) => {
    // Check if conversation already exists
    const existing = conversations.find(c => 
      c.other_participant?.user_id === contact.user_id
    );
    
    if (existing) {
      loadConversation(existing);
    } else {
      // Create placeholder conversation
      const newConv = {
        conversation_id: `${user.user_id}_${contact.user_id}`,
        other_participant: contact,
        last_message: null,
        unread_count: 0
      };
      setActiveConversation(newConv);
      setMessages([]);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeConversation) return;
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/messaging/send/${activeConversation.conversation_id}/attachment`,
        formData,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("File sent");
      loadConversation(activeConversation);
    } catch (error) {
      toast.error("Failed to upload file");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="messages-page">
      {/* Header */}
      <header className="bg-[#0A0A0A]/80 backdrop-blur-sm border-b border-white/[0.08] px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-white/50 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-heading text-xl font-bold text-white flex items-center gap-2">
                <MessageSquare className="w-6 h-6 text-gold" /> Messages
              </h1>
              {unreadCount > 0 && (
                <p className="text-gold text-sm">{unreadCount} unread</p>
              )}
            </div>
          </div>
          <Button
            onClick={() => setShowNewMessage(true)}
            className="bg-gold hover:bg-gold/90 text-black rounded-none"
          >
            <Plus className="w-4 h-4 mr-2" /> New Message
          </Button>
        </div>
      </header>

      <div className="flex h-[calc(100vh-73px)]">
        {/* Conversation list - hidden on mobile when viewing chat */}
        <aside className={`${mobileShowChat ? 'hidden md:block' : 'w-full md:w-80'} md:w-80 border-r border-white/[0.08] overflow-y-auto`}>
          {conversations.length > 0 ? (
            conversations.map((conv) => (
              <ConversationItem
                key={conv.conversation_id}
                conversation={conv}
                isActive={activeConversation?.conversation_id === conv.conversation_id}
                onClick={() => loadConversation(conv)}
              />
            ))
          ) : (
            <div className="p-6 text-center">
              <MessageSquare className="w-12 h-12 text-white/30 mx-auto mb-3" />
              <p className="text-white/50">No conversations yet</p>
              <Button
                onClick={() => setShowNewMessage(true)}
                className="mt-4 bg-gold hover:bg-gold/90 text-black rounded-none"
              >
                Start a Conversation
              </Button>
            </div>
          )}
        </aside>

        {/* Message area - full width on mobile when viewing chat */}
        <main className={`${mobileShowChat ? 'flex' : 'hidden md:flex'} flex-1 flex-col`}>
          {activeConversation ? (
            <>
              {/* Conversation header with mobile back button */}
              <div className="p-4 border-b border-white/[0.08] flex items-center gap-3">
                {/* Mobile back button */}
                <button 
                  onClick={handleMobileBack}
                  className="md:hidden p-2 -ml-2 text-white/50 hover:text-white"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <UserAvatar user={activeConversation.other_participant} />
                <div className="flex-1 min-w-0">
                  <h2 className="text-white font-semibold truncate">
                    {activeConversation.other_participant?.display_name || 
                     activeConversation.other_participant?.name || 
                     activeConversation.other_participant?.company_name || "Unknown"}
                  </h2>
                  <p className="text-xs text-white/50">
                    {userTypeConfig[activeConversation.other_participant?.user_type]?.label || "Member"}
                  </p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4">
                {messages.length > 0 ? (
                  messages.map((msg) => (
                    <MessageBubble
                      key={msg.message_id}
                      message={msg}
                      isMine={msg.sender_id === user?.user_id}
                    />
                  ))
                ) : (
                  <div className="text-center py-8">
                    <p className="text-white/40">No messages yet. Say hello!</p>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <form onSubmit={handleSend} className="p-4 border-t border-white/[0.08] bg-[#0A0A0A]/80 backdrop-blur-sm">
                <div className="flex gap-2">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleFileUpload}
                    />
                    <div className="w-10 h-10 bg-black border border-white/[0.08] flex items-center justify-center text-white/50 hover:text-white hover:border-white/[0.15] transition-all duration-300">
                      <Paperclip className="w-5 h-5" />
                    </div>
                  </label>
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 bg-black border-white/[0.08] text-white"
                  />
                  <Button
                    type="submit"
                    disabled={sending || !newMessage.trim()}
                    className="bg-gold hover:bg-gold/90 text-black rounded-none"
                  >
                    <Send className="w-5 h-5" />
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 text-white/30 mx-auto mb-4" />
                <p className="text-white/40">Select a conversation or start a new one</p>
              </div>
            </div>
          )}
        </main>
      </div>

      <ContactSearchModal
        isOpen={showNewMessage}
        onClose={() => setShowNewMessage(false)}
        onSelect={handleStartConversation}
        currentUserId={user?.user_id}
      />
    </div>
  );
}
